import type { HTMLAttributes, ReactNode } from "react";

/**
 * The shell primitives.
 *
 * These carry the product's identity, which is why none of them came from a component kit:
 * a framed shell with one plate and hairline-divided panes is not a shape any kit ships.
 * docs/03-design-system.md §2.
 */

/** Sits on the shell ground, not inside the plate — navigation belongs to the frame. */
export function Rail({ children, label = "Sections" }: { children: ReactNode; label?: string }) {
  return (
    <nav className="rail" aria-label={label}>
      {children}
    </nav>
  );
}

/** Exactly one per screen. The frame is #root's padding; this is what sits in it. */
export function Plate({ children }: { children: ReactNode }) {
  return <main className="plate">{children}</main>;
}

/**
 * Panes divide the plate; they never float in it. No gaps, no second radius.
 *
 * Pointer and focus handlers are forwarded because the queue needs them to hold its sort
 * order while an agent is about to click a row. A primitive that refuses the props its
 * callers need is a primitive its callers route around, which is how the shell ended up
 * hand-written in the app instead of living here.
 */
export function Pane({
  children,
  kind,
  label,
  as: As = "section",
  ...handlers
}: {
  children: ReactNode;
  kind?: "queue" | "thread" | "context" | "steps" | "auth";
  label?: string;
  as?: "section" | "nav" | "aside";
} & Pick<
  HTMLAttributes<HTMLElement>,
  "onMouseEnter" | "onMouseLeave" | "onFocusCapture" | "onBlurCapture"
>) {
  return (
    <As className={kind ? `pane pane-${kind}` : "pane"} aria-label={label} {...handlers}>
      {children}
    </As>
  );
}

/** A flex row, not position:sticky — sticky inside a scroller jitters on trackpads. */
export function PaneHeader({ children }: { children: ReactNode }) {
  return <header className="pane-head">{children}</header>;
}

/**
 * Three empties, not one.
 *
 * First-run teaches, filtered-empty offers to clear the filter, error-empty offers a retry.
 * An outage rendered as "nothing here" is a lie an agent acts on. docs/03 §9.
 */
export function EmptyState({
  title,
  body,
  action,
}: {
  title?: string;
  body: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="empty">
      {title && <p className="empty-title">{title}</p>}
      <p className="empty-body">{body}</p>
      {action && (
        <button type="button" className="btn" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}

/** Shaped like the thing that is coming. No spinners on lists — a spinner says nothing. */
export function Skeleton({ width = "100%", lines = 1 }: { width?: string; lines?: number }) {
  return (
    <>
      {Array.from({ length: lines }, (_, i) => `skeleton-${i}`).map((key, i) => (
        <div
          key={key}
          className="skeleton"
          style={{ inlineSize: i % 2 && lines > 1 ? "60%" : width }}
        />
      ))}
    </>
  );
}

/** Semantic colour is always paired with a shape — hue is never load-bearing. docs/03 §4. */
export function StatusPill({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "brand" | "ok" | "warn" | "danger";
  children: ReactNode;
}) {
  return <span className={tone === "neutral" ? "pill" : `pill ${tone}`}>{children}</span>;
}
